import { ChangeEvent, FC, PropsWithChildren, useContext, useMemo } from "react";
import * as React from "react";
import { TextInputContext } from "./TextInput";

export type QESOptionsListProps = OptionsListProps;

export interface OptionsListOption {
  key: string;
  value: string;
}

interface OptionsListProps {
  options: OptionsListOption[];
  showUnmatched?: boolean;
  matchFunction?: (option: OptionsListOption, inputValue: string) => boolean;
  textIfNoMatch?: string;
}

/**
 * A component meant to be nested as a child of QESTextInput that provides a styled, filterable list of options to the user.
 */
const QESOptionsList: FC<PropsWithChildren<QESOptionsListProps>> = ({ options, showUnmatched, matchFunction, textIfNoMatch }) => {
  const inputContext = useContext(TextInputContext);

  const activeMatchFunction = matchFunction || ((option: OptionsListOption, inputValue: string) => option.value.toLowerCase().includes(inputValue.toLowerCase()));

  const handleOptionClick = (option: OptionsListOption) => {
    inputContext.onChange({ target: { value: option.value } } as ChangeEvent<HTMLInputElement>);
    inputContext.setFocused(false);
  }

  const getMatchingOptions = useMemo(() => {
    if (!inputContext.value) {
      return options;
    }
    const filteredOptions = options.filter(option => activeMatchFunction(option, inputContext.value.toString()));
    filteredOptions.sort();
    return filteredOptions;
  }, [options, inputContext.value, matchFunction]);

  const getNonMatchingOptions = useMemo(() => {
    if (!inputContext.value) {
      return [];
    }
    const filteredOptions = options.filter(option => !activeMatchFunction(option, inputContext.value.toString()));
    filteredOptions.sort();
    return filteredOptions;
  }, [options, inputContext.value, matchFunction]);

  if (!inputContext.focused) {
    return null;
  }

  return (
    <div className="qes-text-input__options-dropdown">
      {getMatchingOptions.map((option) => (
        <button key={option.key} onMouseDown={() => handleOptionClick(option)}
                className="qes-button new-action-menu-item"
        >{option.value}</button>
      ))}
      {!showUnmatched && getMatchingOptions.length === 0 && (
        <button disabled className="qes-button new-action-menu-item qes-text-input__options-dropdown-non-matching"
        >{textIfNoMatch || "No options found."}</button>
      )}
      {showUnmatched && (
        <>
          <div className="qes-text-input__options-dropdown-divider"/>
        {getNonMatchingOptions.map((option) => (
          <button key={option.key} onMouseDown={() => handleOptionClick(option)}
                  className="qes-button new-action-menu-item qes-text-input__options-dropdown-non-matching"
          >{option.value}</button>
        ))}
        </>
      )}
    </div>
  )
}

export default QESOptionsList;